import { Link, type LinkProps } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'

import { cn } from '@/lib/utils'

interface BackButtonProps {
  to?: NonNullable<LinkProps['to']>
  /** The parent's own search, for a parent that has more than one view (a basket's list tab). */
  search?: LinkProps['search']
  className?: string
}

/**
 * The chevron that leads every sub-page title row (TitleRow's `back` prop).
 *
 * A Link to a known parent, not `history.back()`: these pages are reachable
 * from a shared URL and from the sidebar, where there is no history to pop and
 * the button would either do nothing or leave the app entirely.
 *
 * A bare chevron, no box, fill or border, as the mock's Notifications head
 * draws it (`.hm-back`: 32px target, 6x12 glyph, 1.8 stroke, `text-primary`).
 * The mock's other back controls disagree (boxed 20px, wash on hover, accent
 * text); Notifications is the canonical one for the shape. The states are the
 * Signal page's (`sig-back`, owner ruling A1 2026-10-29: no fade): hover turns
 * the chevron the link gold (mock `#e6c377`) and slides it 2px back, a press
 * scales it to .92, and focus is the white control ring (A2). The slide and the
 * press are reduced-motion-safe. A 24px chevron at 1.8 stroke draws the 6x12
 * glyph.
 *
 * The 32px square is the pointer target already (above the 24px WCAG 2.5.8
 * floor); an invisible `::before` grows it 8px a side (48) below `lg`, for a
 * finger. Nothing moves.
 */
export default function BackButton({ to = '/', search, className }: BackButtonProps) {
  return (
    <Link
      to={to}
      {...(search === undefined ? {} : { search })}
      aria-label="Back"
      className={cn(
        'group/back relative inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-foreground transition-[color,scale] hover:text-link focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:active:scale-92',
        "touch-manipulation before:absolute before:inset-0 before:content-[''] max-lg:before:-inset-2",
        className,
      )}
    >
      <ChevronLeft
        className="size-6 transition-[translate] duration-med ease-out group-hover/back:motion-safe:-translate-x-(--motion-nudge) group-focus-visible/back:motion-safe:-translate-x-(--motion-nudge)"
        strokeWidth={1.8}
        aria-hidden="true"
      />
    </Link>
  )
}
